import { useParams, Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ContentPage } from "../components/ContentPage";
import { CTASection } from "../components/CTASection";
import { INDUSTRY_PAGES } from "../data/site";
import { INDUSTRY_SERVICE_PAGES } from "../data/industryServicePages";
import { FULL_INDUSTRY_PAGES } from "../data/industryPagesFull";
import IndustryLandingPage from "./IndustryLandingPage";

// 301-style consolidation: old duplicate money-page URLs → canonical industry pages.
const REDIRECTS = {
  "managed-it-for-cpa-firms-atlanta": "cpa-accounting-firms-atlanta",
  "managed-it-for-architecture-firms-atlanta": "architecture-engineering-firms-atlanta",
};

export default function IndustryDetail() {
  const { slug } = useParams();
  if (REDIRECTS[slug]) return <Navigate to={`/industries/${REDIRECTS[slug]}`} replace />;
  const full = FULL_INDUSTRY_PAGES[slug];
  if (full) return <IndustryLandingPage data={full} slug={slug} />;
  const data = INDUSTRY_PAGES[slug];
  if (!data) {
    const svc = INDUSTRY_SERVICE_PAGES[slug];
    if (svc) return <ContentPage data={svc} path={`/industries/${slug}`} eyebrow={svc.group} />;
    return <Navigate to="/industries" replace />;
  }

  const Icon = data.icon;

  return (
    <div data-testid={`industry-detail-${slug}`}>
      <Seo title={data.metaTitle} description={data.metaDescription} path={`/industries/${slug}`} />

      {/* HERO */}
      <section className="pt-32 sm:pt-40 pb-16 px-5 sm:px-8 bg-mist">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">{data.eyebrow}</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-charcoal leading-tight">
              {data.title}
            </h1>
            {data.intro.map((p) => (
              <p key={p} className="mt-5 text-lg text-slate-600 leading-relaxed">{p}</p>
            ))}
            <div className="mt-8">
              <Link to="/contact" data-testid="industry-cta-top"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors">
                Book a Free IT Assessment <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <img src={data.image} alt={data.title} className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
          </Reveal>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="py-20 sm:py-28 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-sm uppercase tracking-widest font-semibold text-teal mb-4">What we solve</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-charcoal">
              The technology challenges we hear most.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            {data.challenges.map((c, i) => (
              <Reveal key={c.title} delay={(i % 2) * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-8">
                  <div className="h-11 w-11 rounded-2xl bg-navy/5 flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-navy" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-charcoal mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES ALIGNMENT + WHY */}
      <section className="py-20 sm:py-28 px-5 sm:px-8 bg-mist">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12">
          <Reveal>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-charcoal mb-6">How we help</h2>
            <div className="space-y-3">
              {data.services.map((s) => (
                <div key={s} className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200 px-5 py-4">
                  <span className="h-6 w-6 rounded-full bg-emerald/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3.5 w-3.5 text-emerald" />
                  </span>
                  <span className="font-medium text-charcoal">{s}</span>
                </div>
              ))}
            </div>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 font-semibold text-navy hover:gap-3 transition-all">
              Explore all services <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-charcoal mb-6">Why firms choose Zentryx</h2>
            <ul className="space-y-4">
              {data.why.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <span className="mt-1 h-5 w-5 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-navy" />
                  </span>
                  <span className="text-slate-700 leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto rounded-3xl bg-navy relative overflow-hidden px-8 sm:px-16 py-16 text-center">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-teal/20 blur-3xl" />
          <Reveal className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">{data.ctaTitle}</h2>
            <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">{data.ctaBody}</p>
            <Link to="/contact"
              className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-emerald hover:bg-emerald-dark text-white font-semibold transition-colors">
              Book Your Free IT Assessment <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
